import { ChevronDown, ChevronUp, GitBranch, Trash2 } from 'lucide-react';
import type { CardMirror } from '@molt/shared';
import { IconButton } from '@/components';
import { useInterfaceLanguage } from '@/lib/interface-language';

export interface MirrorDisplayControlsProps {
  readonly card: CardMirror;
  readonly expanded: boolean;
  readonly onExpandedChange: (expanded: boolean) => void;
  readonly onRemove?: (() => void) | undefined;
}

export function MirrorDisplayControls({
  card,
  expanded,
  onExpandedChange,
  onRemove,
}: MirrorDisplayControlsProps) {
  const { t } = useInterfaceLanguage();

  return (
    <div className="relative z-[3] flex flex-col gap-1">
      <div className="flex items-center gap-1">
        <span className="inline-flex min-w-0 max-w-full items-center gap-1 rounded-full border border-border bg-surface-raised px-2 py-0.5 text-xs font-medium text-ink">
          <GitBranch aria-hidden="true" className="size-3" />
          <span className="truncate">
            {t('board.card.mirrorControls.fromSource', {
              board: card.sourceBoard.title,
            })}
          </span>
        </span>
        <IconButton
          label={t(
            expanded
              ? 'board.card.mirrorControls.collapse'
              : 'board.card.mirrorControls.expand',
            { title: card.title },
          )}
          size="sm"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onExpandedChange(!expanded);
          }}
        >
          {expanded ? (
            <ChevronUp aria-hidden="true" className="size-3.5" />
          ) : (
            <ChevronDown aria-hidden="true" className="size-3.5" />
          )}
        </IconButton>
        {onRemove ? (
          <IconButton
            label={t('board.card.mirrorControls.remove', { title: card.title })}
            size="sm"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onRemove();
            }}
          >
            <Trash2 aria-hidden="true" className="size-3.5" />
          </IconButton>
        ) : null}
      </div>

      {card.sourceDeleted ? (
        <div className="rounded-card border border-bad/40 bg-surface-raised px-2 py-1 text-xs font-medium text-ink">
          {t('board.card.mirrorControls.sourceDeleted')}
        </div>
      ) : card.sourceArchived ? (
        <div className="rounded-card border border-warn/40 bg-surface-raised px-2 py-1 text-xs font-medium text-ink">
          {t('board.card.mirrorControls.sourceArchived')}
        </div>
      ) : null}

      {expanded ? (
        <div className="rounded-card border border-border bg-surface-raised/95 px-2 py-1 text-xs text-ink">
          {card.sourceDeleted
            ? t('board.card.mirrorControls.deletedBody')
            : t('board.card.mirrorControls.activeBody', {
                board: card.sourceBoard.title,
              })}
        </div>
      ) : null}
    </div>
  );
}
